import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api, formatApiError, fmtEUR } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import AddressAutocomplete from "./AddressAutocomplete";
import MapPreview from "./MapPreview";
import { SupplierExecFields } from "./suppliers/SupplierExecFields";
import { isDispoType, isDispo } from "./dispo/dispoUtils";
import { DispoAdminPanel } from "./dispo/DispoAdminPanel";
import { DispoModeToggle } from "./dispo/DispoModeToggle";
import {
  AlertTriangle, Plus, Trash2,
  CalendarDays, UserRound, Building2, MapPin, Users as UsersIcon,
  Euro, CreditCard, ArrowLeftRight, StickyNote, Lock, Globe2,
} from "lucide-react";

const TOURVOYAGERS_URL = process.env.REACT_APP_TOURVOYAGERS_URL;
const tourvoyagersLink = (b) => {
  const u = new URL("importa", TOURVOYAGERS_URL);
  u.searchParams.set("striano_id", b.id);
  if (b.booking_number) u.searchParams.set("booking_number", b.booking_number);
  return u.toString();
};
const openTourvoyagers = async (e, b) => {
  e.preventDefault();
  const w = window.open("about:blank", "_blank");
  try {
    await api.post(`/tourvoyagers/share/${b.id}`);
    if (w) { w.opener = null; w.location.href = tourvoyagersLink(b); }
    else window.location.href = tourvoyagersLink(b);
  } catch (err) {
    if (w) w.close();
    toast.error(formatApiError(err));
  }
};

const STATUSES = ["Preventivo", "Confermata", "Assegnata", "In corso", "Completata", "Cancellata", "No show"];

export default function BookingModal({ booking, onClose }) {
  const { user } = useAuth();
  const isDriver = user?.role === "autista";
  const isEdit = !!(booking && booking.id);
  const [customers, setCustomers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [services, setServices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    service_type: "Transfer aeroporto",
    date: new Date().toISOString().slice(0, 10),
    time: "09:00",
    passengers: 1,
    luggage: 0,
    customer_id: "",
    partner_id: "",
    passenger_name: "",
    phone: "",
    email: "",
    pickup: "",
    destination: "",
    stops: "",
    route_notes: "",
    vehicle_id: "",
    driver_id: "",
    price: 0,
    supplier_price: null,
    driver_price: 0,
    driver_cost: 0,
    commission: 0,
    other_costs: 0,
    driver_collected: 0,
    driver_expenses: 0,
    expense_items: [],
    driver_advanced: 0,
    flight_number: "",
    hotel_name: "",
    notes: "",
    special_requests: "",
    status: "Preventivo",
    payment_status: "Da incassare",
    payment_method: "",
    related_booking_id: null,
    ...booking,
  });

  useEffect(() => {
    if (isDriver) {
      Promise.allSettled([
        api.get("/drivers"),
        api.get("/vehicles"),
        api.get("/services-catalog"),
      ]).then(([d, v, s]) => {
        if (d.status === "fulfilled") setDrivers(d.value.data);
        if (v.status === "fulfilled") setVehicles(v.value.data);
        if (s.status === "fulfilled") setServices(s.value.data);
      });
      return;
    }
    Promise.allSettled([
      api.get("/customers"),
      api.get("/drivers"),
      api.get("/vehicles"),
      api.get("/services-catalog"),
      api.get("/suppliers"),
    ]).then(([c, d, v, s, sp]) => {
      if (c.status === "fulfilled") setCustomers(c.value.data);
      if (d.status === "fulfilled") setDrivers(d.value.data);
      if (v.status === "fulfilled") setVehicles(v.value.data);
      if (s.status === "fulfilled") setServices(s.value.data);
      if (sp.status === "fulfilled") setSuppliers(sp.value.data);
    });
    // Backfill driver_price da price se prenotazione esistente non lo ha (retrocompat)
    if (booking && (!booking.driver_price || booking.driver_price === 0) && booking.price) {
      setF(s => ({ ...s, driver_price: booking.price }));
    }
  }, [isDriver]);

  // Auto-compute route when both endpoints geocoded
  useEffect(() => {
    if (f.pickup_lat != null && f.pickup_lon != null && f.destination_lat != null && f.destination_lon != null) {
      api.get("/geocode/route", { params: { from_lat: f.pickup_lat, from_lon: f.pickup_lon, to_lat: f.destination_lat, to_lon: f.destination_lon } })
        .then(r => {
          if (r.data?.distance_km != null) setF(s => ({ ...s, distance_km: r.data.distance_km, duration_min: r.data.duration_min, _geometry: r.data.geometry }));
        }).catch(() => {});
    }
  }, [f.pickup_lat, f.pickup_lon, f.destination_lat, f.destination_lon]);

  const hasPartner = !!(f.partner_id);
  const hasDriver = !!(f.driver_id);
  // Prezzo al fornitore effettivo (usato nel margine SOLO se è presente un fornitore)
  const effectiveSupplierPrice = hasPartner ? (Number(f.supplier_price) || 0) : 0;

  // Base autista effettiva: driver_price (fallback a price per retrocompat SOLO se c'è un driver assegnato).
  // Se NON c'è un driver assegnato, la base è ZERO → nessun compenso autista automatico.
  // Questo rispetta la regola: FORNITORE + no DRIVER → Compenso autista = €0.
  const effectiveDriverBase = useMemo(() => {
    if (!hasDriver) return 0;
    const dp = +f.driver_price || 0;
    return dp > 0 ? dp : (+f.price || 0);
  }, [hasDriver, f.driver_price, f.price]);
  const computedDriverCost = useMemo(() => Math.round(effectiveDriverBase * 50) / 100, [effectiveDriverBase]);
  const driverPct = (+f.price || 0) > 0 ? Math.round((computedDriverCost / (+f.price || 1)) * 100) : 50;

  // Il margine include costo fornitore solo quando c'è un fornitore, e compenso autista solo quando c'è un driver.
  // DRIVER ≠ FORNITORE: nessun doppio costo, nessun compenso phantom.
  const margin = useMemo(() =>
    (+f.price || 0) - computedDriverCost - (+f.commission || 0) - (+f.other_costs || 0) - effectiveSupplierPrice,
  [f.price, computedDriverCost, f.commission, f.other_costs, effectiveSupplierPrice]);
  const marginPct = f.price > 0 ? (margin / +f.price) * 100 : 0;

  useEffect(() => {
    if (isDriver) return;
    // Sync driver_cost al computato. Quando non c'è driver assegnato, driver_cost = 0 (ZERO compenso phantom).
    setF(s => ({ ...s, driver_cost: computedDriverCost }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computedDriverCost, isDriver]);

  // Se l'utente deseleziona il fornitore, svuoto supplier_price per rispettare la regola:
  // "Driver senza Fornitore → Prezzo al fornitore resta vuoto, NESSUN movimento nei conteggi fornitori".
  useEffect(() => {
    if (!hasPartner && f.supplier_price != null && f.supplier_price !== "") {
      setF(s => ({ ...s, supplier_price: null }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPartner]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  // Verifica turno autista per la data selezionata
  const [driverShift, setDriverShift] = useState(null);
  const [driverAbsenceSub, setDriverAbsenceSub] = useState(null);
  useEffect(() => {
    if (!f.driver_id || !f.date) { setDriverShift(null); setDriverAbsenceSub(null); return; }
    const [y, m] = f.date.split("-");
    api.get("/shifts", { params: { year: Number(y), month: Number(m), driver_id: f.driver_id } })
      .then(r => {
        const dayNum = parseInt(f.date.slice(-2), 10);
        const row = (r.data || []).find(x => x.driver_id === f.driver_id);
        if (!row) { setDriverShift(null); setDriverAbsenceSub(null); return; }
        const code = (row.days || {})[String(dayNum)] || null;
        setDriverShift(code);
        if (["SICK","VACATION","LEAVE"].includes(code) && row.substitutes && row.substitutes[String(dayNum)]) {
          const sid = row.substitutes[String(dayNum)];
          const sub = drivers.find(d => d.id === sid);
          setDriverAbsenceSub(sub ? `${sub.first_name} ${sub.last_name}` : sid);
        } else setDriverAbsenceSub(null);
      })
      .catch(() => { setDriverShift(null); setDriverAbsenceSub(null); });
  }, [f.driver_id, f.date, drivers]);

  const dispoType = isDispoType(f.service_type);
  // Solo visualizzazione: interna/esterna non viene salvata e non azzera valori
  const [dispoMode, setDispoMode] = useState(null);
  const dispoExt = dispoType && (dispoMode || (hasPartner ? "external" : "internal")) === "external";
  const dispoInt = dispoType && !dispoExt;
  const rateOrNull = (v) => (v === "" || v == null ? null : Number(v) || 0);
  const save = async () => {
    if (dispoType && !isEdit && (rateOrNull(f.dispo_client_rate) == null || (!dispoExt && rateOrNull(f.dispo_driver_rate) == null))) {
      toast.error(dispoExt ? "DISPO: inserisci tariffa oraria cliente" : "DISPO: inserisci tariffa oraria cliente e tariffa oraria driver");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...f,
        dispo_client_rate: dispoType ? rateOrNull(f.dispo_client_rate) : (f.dispo_client_rate ?? null),
        dispo_driver_rate: dispoType ? rateOrNull(f.dispo_driver_rate) : (f.dispo_driver_rate ?? null),
        passengers: Number(f.passengers) || 1,
        luggage: Number(f.luggage) || 0,
        price: Number(f.price) || 0,
        // supplier_price: valorizzato SOLO se c'è un fornitore. Altrimenti null (nessun movimento nei conteggi fornitori).
        supplier_price: hasPartner && f.supplier_price !== null && f.supplier_price !== "" ? (Number(f.supplier_price) || 0) : null,
        driver_cost: Number(f.driver_cost) || 0,
        commission: Number(f.commission) || 0,
        other_costs: Number(f.other_costs) || 0,
        driver_collected: Number(f.driver_collected) || 0,
        driver_expenses: Number(f.driver_expenses) || 0,
        driver_advanced: Number(f.driver_advanced) || 0,
      };
      delete payload._return_from;
      delete payload._return_from_number;
      delete payload._geometry;
      let saved;
      if (isEdit) {
        const r = await api.patch(`/bookings/${booking.id}`, payload);
        saved = r.data;
      } else {
        const r = await api.post("/bookings", payload);
        saved = r.data;
      }
      onClose(saved || true);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent data-testid="booking-modal" className="max-w-6xl max-h-[94vh] overflow-y-auto bg-[#070A12] border-amber-500/10 text-slate-200 p-0">
        <DialogHeader className="px-7 pt-6 pb-2">
          <DialogTitle className="font-display text-[28px] text-white tracking-tight">
            {isEdit ? "Modifica Prenotazione" : "Nuova Prenotazione"}
          </DialogTitle>
          <div className="text-sm text-slate-500 mt-0.5">Inserisci i dettagli del servizio</div>
        </DialogHeader>

        <div className="px-7 pb-7 grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* ----- COLONNA SINISTRA ----- */}
          <div className="lg:col-span-2 space-y-4">
            {dispoType && !isDriver && <DispoModeToggle mode={dispoExt ? "external" : "internal"} onChange={setDispoMode} />}
            {/* DATI SERVIZIO */}
            <Card icon={CalendarDays} title="Dati Servizio">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Tipologia">
                  <Select value={f.service_type} onValueChange={(v) => set("service_type", v)}>
                    <SelectTrigger data-testid="booking-service-type" className={fieldCls}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-950 border-slate-800">
                      {services.map(s => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Data"><Input data-testid="booking-date" type="date" value={f.date} onChange={(e)=>set("date",e.target.value)} className={fieldCls}/></Field>
                <Field label="Ora"><Input data-testid="booking-time" type="time" value={f.time} onChange={(e)=>set("time",e.target.value)} className={fieldCls}/></Field>
                <Field label="Passeggeri"><Input type="number" min="1" value={f.passengers} onChange={(e)=>set("passengers",e.target.value)} className={fieldCls}/></Field>
                <Field label="Bagagli"><Input type="number" min="0" value={f.luggage} onChange={(e)=>set("luggage",e.target.value)} className={fieldCls}/></Field>
                <Field label="N° Volo"><Input value={f.flight_number} onChange={(e)=>set("flight_number",e.target.value)} className={fieldCls}/></Field>
                <Field label="Hotel"><Input value={f.hotel_name} onChange={(e)=>set("hotel_name",e.target.value)} className={fieldCls}/></Field>
                <Field label="Stato">
                  <Select value={f.status} onValueChange={(v) => set("status", v)}>
                    <SelectTrigger data-testid="booking-status" className={fieldCls}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-slate-950 border-slate-800">
                      {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </Card>

            {/* CLIENTE */}
            <Card icon={UserRound} title="Cliente">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Cliente" className="col-span-2 md:col-span-1">
                  <Select value={f.customer_id || "none"} onValueChange={(v) => set("customer_id", v === "none" ? "" : v)}>
                    <SelectTrigger className={fieldCls}><SelectValue placeholder="Seleziona"/></SelectTrigger>
                    <SelectContent className="bg-slate-950 border-slate-800">
                      <SelectItem value="none">-- Nessuno --</SelectItem>
                      {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Passeggero"><Input data-testid="booking-passenger" value={f.passenger_name} onChange={(e)=>set("passenger_name",e.target.value)} className={fieldCls}/></Field>
                <Field label="Telefono"><Input value={f.phone} onChange={(e)=>set("phone",e.target.value)} className={fieldCls}/></Field>
                <Field label="Email"><Input value={f.email} onChange={(e)=>set("email",e.target.value)} className={fieldCls}/></Field>
              </div>
            </Card>

            {/* FORNITORE — ring oro quando è un elemento chiave */}
            {!(dispoInt && !hasPartner) && <Card
              icon={Building2}
              title="Fornitore"
              highlighted={hasPartner}
              testid="booking-fornitore-card"
              headerRight={
                <a
                  data-testid="add-partner-link"
                  href="/fornitori"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-mono-num font-bold px-3 py-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 transition-colors"
                >
                  <Plus size={12} strokeWidth={3}/> Aggiungi fornitore
                </a>
              }
            >
              <div className="grid grid-cols-1 gap-3">
                <Field label="Fornitore">
                  <Select value={f.partner_id || "none"} onValueChange={(v) => setF(s => ({ ...s, partner_id: v === "none" ? "" : v,
                    ...(v !== s.partner_id ? { partner_driver_id: null, partner_driver_name: null, partner_driver_phone: null, partner_vehicle_id: null, partner_vehicle_name: null, partner_vehicle_plate: null } : {}) }))}>
                    <SelectTrigger data-testid="booking-partner" className={fieldCls}><SelectValue placeholder="Nessuno"/></SelectTrigger>
                    <SelectContent className="bg-slate-950 border-slate-800 max-h-72">
                      <SelectItem value="none">Nessuno</SelectItem>
                      {(suppliers || []).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                {hasPartner && (
                  <SupplierExecFields supplier={suppliers.find(s => s.id === f.partner_id)} f={f} setF={setF}
                    onSupplierUpdated={(ns) => setSuppliers(list => list.map(x => x.id === ns.id ? ns : x))} />
                )}
                <div className="text-[11px] text-slate-500 leading-snug">
                  Il Fornitore è separato dal Driver. Quando selezioni un fornitore, il campo <span className="text-amber-300 font-semibold">{dispoExt ? "Prezzo per fornitore" : "Prezzo al fornitore"}</span> nella sezione Economico diventa attivo e il valore confluisce automaticamente nei Conteggi → Dare/Avere → Fornitore.
                </div>
                {dispoInt && hasPartner && (
                  <div className="text-[11px] text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2" data-testid="dispo-internal-partner-note">
                    Questa DISPO ha un fornitore selezionato: il fornitore e il suo prezzo restano salvati. Per visualizzarla come esterna scegli «DISPO ESTERNA».
                  </div>
                )}
              </div>
            </Card>}

            {/* PERCORSO */}
            <Card icon={MapPin} title="Percorso">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <AddressAutocomplete
                  label="Partenza" testid="booking-pickup"
                  value={{ display: f.pickup, lat: f.pickup_lat, lon: f.pickup_lon, place_id: f.pickup_place_id }}
                  onChange={(r) => setF(s => ({ ...s, pickup: r.display || "", pickup_lat: r.lat ?? null, pickup_lon: r.lon ?? null, pickup_place_id: r.place_id ?? null }))}
                />
                <AddressAutocomplete
                  label="Destinazione" testid="booking-destination"
                  value={{ display: f.destination, lat: f.destination_lat, lon: f.destination_lon, place_id: f.destination_place_id }}
                  onChange={(r) => setF(s => ({ ...s, destination: r.display || "", destination_lat: r.lat ?? null, destination_lon: r.lon ?? null, destination_place_id: r.place_id ?? null }))}
                />
                {f.distance_km != null && (
                  <div className="md:col-span-2 rounded-lg bg-slate-900/60 border border-slate-800 px-3 py-2 text-xs text-slate-400 font-mono-num flex items-center gap-4">
                    <span>Distanza: <span className="text-amber-400">{f.distance_km} km</span></span>
                    <span>Durata stimata: <span className="text-amber-400">{f.duration_min} min</span></span>
                  </div>
                )}
                {(f.pickup_lat != null && f.destination_lat != null) && (
                  <div className="md:col-span-2">
                    <MapPreview
                      from={{ lat: f.pickup_lat, lon: f.pickup_lon }}
                      to={{ lat: f.destination_lat, lon: f.destination_lon }}
                      geometry={f._geometry}
                    />
                  </div>
                )}
                <Field label="Fermate intermedie" className="md:col-span-2">
                  <div className="flex gap-2">
                    <Input value={f.stops} onChange={(e)=>set("stops",e.target.value)} className={`${fieldCls} flex-1`} placeholder="Elenca fermate separate da virgola"/>
                  </div>
                </Field>
                <Field label="Note percorso" className="md:col-span-2"><Textarea rows={2} value={f.route_notes} onChange={(e)=>set("route_notes",e.target.value)} className={fieldCls} placeholder="Note sul percorso, indicazioni particolari..."/></Field>
              </div>
            </Card>

            {/* ASSEGNAZIONE */}
            <Card icon={UsersIcon} title="Assegnazione">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Veicolo">
                  <Select value={f.vehicle_id || "none"} onValueChange={(v) => set("vehicle_id", v === "none" ? "" : v)}>
                    <SelectTrigger className={fieldCls}><SelectValue placeholder="Seleziona"/></SelectTrigger>
                    <SelectContent className="bg-slate-950 border-slate-800">
                      <SelectItem value="none">-- Nessuno --</SelectItem>
                      {vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.brand} {v.model} · {v.plate}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {(() => {
                    const veh = vehicles.find(v => v.id === f.vehicle_id);
                    const ids = veh ? (veh.assigned_driver_ids?.length ? veh.assigned_driver_ids : (veh.current_driver_id ? [veh.current_driver_id] : [])) : [];
                    const list = ids.map(id => drivers.find(d => d.id === id)).filter(Boolean);
                    if (!list.length) return null;
                    return (
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid="booking-vehicle-drivers">
                        <span className="text-[10px] uppercase tracking-wider text-slate-500">Autisti del veicolo:</span>
                        {list.map(d => (
                          <button key={d.id} type="button" onClick={() => set("driver_id", d.id)} data-testid={`booking-vehicle-driver-${d.id}`}
                            className={`rounded-full border px-2 py-0.5 text-[11px] ${f.driver_id === d.id ? "border-amber-400 bg-amber-500/15 text-amber-200" : "border-slate-700 text-slate-300 hover:border-amber-500/50"}`}>
                            {d.first_name} {d.last_name}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </Field>
                {dispoExt ? (
                  <div className="text-[11px] text-slate-400 self-end pb-2" data-testid="dispo-external-driver-note">
                    {hasDriver ? <>DISPO esterna: l'autista interno già assegnato ({(drivers.find(d => d.id === f.driver_id) || {}).first_name || "—"}) resta salvato (il suo compenso resta nel margine) ma non viene mostrato. Per gestirlo scegli «DISPO INTERNA».</> : "DISPO esterna: il servizio è eseguito dal fornitore."}
                  </div>
                ) : <Field label="Autista">
                  <Select value={f.driver_id || "none"} onValueChange={(v) => set("driver_id", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="booking-driver" className={fieldCls}><SelectValue placeholder="Seleziona"/></SelectTrigger>
                    <SelectContent className="bg-slate-950 border-slate-800">
                      <SelectItem value="none">-- Nessuno --</SelectItem>
                      {drivers.map(d => <SelectItem key={d.id} value={d.id}>{d.first_name} {d.last_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {driverShift === "REST" && (
                    <ShiftWarning testid="driver-rest-warning" tone="rose" title="Attenzione" body={<>— l'autista risulta in <b>RIPOSO</b> in Turnistica per il {f.date}. Verifica la disponibilità prima di confermare.</>}/>
                  )}
                  {driverShift === "SICK" && (
                    <ShiftWarning testid="driver-sick-warning" tone="rose" title="Autista in MALATTIA" body={<> il {f.date}.{driverAbsenceSub ? <> Sostituto: <b>{driverAbsenceSub}</b>.</> : " Nessun sostituto assegnato."} Verifica in Turnistica.</>}/>
                  )}
                  {driverShift === "VACATION" && (
                    <ShiftWarning testid="driver-vacation-warning" tone="indigo" title="Autista in FERIE" body={<> il {f.date}.{driverAbsenceSub ? <> Sostituto: <b>{driverAbsenceSub}</b>.</> : " Nessun sostituto assegnato."}</>}/>
                  )}
                  {driverShift === "LEAVE" && (
                    <ShiftWarning testid="driver-leave-warning" tone="purple" title="Autista in PERMESSO" body={<> il {f.date}.{driverAbsenceSub ? <> Sostituto: <b>{driverAbsenceSub}</b>.</> : " Nessun sostituto assegnato."}</>}/>
                  )}
                  {driverShift === "NIGHT" && (
                    <div className="mt-2 flex items-center gap-2 rounded-lg border border-sky-500/40 bg-sky-500/10 p-2 text-xs text-sky-200">
                      <span className="w-2 h-2 rounded-full bg-sky-400"/> Turno NOTTE assegnato in Turnistica per il {f.date}
                    </div>
                  )}
                  {driverShift === "DAY" && (
                    <div className="mt-2 flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-2 text-xs text-emerald-200">
                      <span className="w-2 h-2 rounded-full bg-emerald-400"/> Turno GIORNO assegnato in Turnistica per il {f.date}
                    </div>
                  )}
                </Field>}
              </div>
            </Card>

            {/* Assegnazione comunicata dal collega destinatario (solo lettura, se booking passato via Scambio) */}
            {(booking?.exchange_driver_name || booking?.exchange_vehicle_name || booking?.exchange_vehicle_plate) && (
              <Card icon={ArrowLeftRight} title="Assegnazione dal collega (via Scambio Servizi)">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-widest font-mono-num">Driver</div>
                    <div className="text-white font-bold mt-0.5" data-testid="booking-exchange-driver">{booking.exchange_driver_name || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-widest font-mono-num">Veicolo</div>
                    <div className="text-white font-bold mt-0.5" data-testid="booking-exchange-vehicle">{booking.exchange_vehicle_name || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-widest font-mono-num">Targa</div>
                    <div className="text-amber-300 font-mono-num font-bold mt-0.5" data-testid="booking-exchange-plate">{booking.exchange_vehicle_plate || "—"}</div>
                  </div>
                </div>
                {booking.exchange_assignment_updated_at && (
                  <div className="text-[10px] text-slate-500 mt-2 font-mono-num">
                    Aggiornato: {new Date(booking.exchange_assignment_updated_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </div>
                )}
              </Card>
            )}
            {/* NOTE */}
            <Card icon={StickyNote} title="Note">
              <Textarea rows={3} placeholder="Note interne" value={f.notes} onChange={(e)=>set("notes",e.target.value)} className={fieldCls}/>
              <Textarea rows={2} placeholder="Richieste speciali" value={f.special_requests} onChange={(e)=>set("special_requests",e.target.value)} className={`${fieldCls} mt-2`}/>
            </Card>
          </div>

          {/* ----- COLONNA DESTRA ----- */}
          <div className="space-y-4">
            {!isDriver && (
              <Card icon={Euro} title="Economico" sticky={!dispoType}>
                <div className="space-y-2.5">
                  {dispoType && (
                    <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 space-y-2" data-testid="booking-dispo-rates">
                      <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">DISPO · tariffe orarie</div>
                      <MoneyRow label="Tariffa oraria cliente (€/ora)" v={f.dispo_client_rate} onChange={(v)=>set("dispo_client_rate",v)} testid="booking-dispo-client-rate"/>
                      {!dispoExt && <MoneyRow label="Tariffa oraria driver (€/ora)" v={f.dispo_driver_rate} onChange={(v)=>set("dispo_driver_rate",v)} testid="booking-dispo-driver-rate"/>}
                      <div className="text-[11px] text-slate-500">{dispoExt ? "Fine e durata non obbligatorie. Alla chiusura: ore fatturabili (regola 10 min) × tariffa → Prezzo cliente." : "Fine e durata non obbligatorie. Alla chiusura: ore fatturabili (regola 10 min) × tariffe → Prezzo cliente e Prezzo driver."}</div>
                    </div>
                  )}
                  {isEdit && isDispo(f) && <DispoAdminPanel booking={f} external={dispoExt} onSaved={(nb) => setF(s => ({ ...s, ...nb }))} />}
                  <MoneyRow label="Prezzo servizio cliente" v={f.price} onChange={(v)=>set("price",v)} testid="booking-price"/>

                  {/* Prezzo al fornitore — attivo solo se è selezionato un fornitore */}
                  {!(dispoInt && !hasPartner) && <MoneyRow
                    label={dispoExt ? "Prezzo per fornitore" : "Prezzo al fornitore"}
                    v={hasPartner ? (f.supplier_price ?? "") : ""}
                    onChange={(v)=>set("supplier_price", v === "" ? null : v)}
                    testid="booking-supplier-price"
                    disabled={!hasPartner}
                    disabledHint="Seleziona prima un fornitore"
                  />}

                  {!dispoExt && <>
                  <MoneyRow label="Prezzo impostato al driver" v={f.driver_price} onChange={(v)=>set("driver_price",v)} testid="booking-driver-price" disabled={!hasDriver} disabledHint="Seleziona prima un autista"/>

                  <div className={`rounded-xl px-3 py-2.5 flex items-center justify-between ${hasDriver ? "bg-emerald-500/5 border border-emerald-500/30" : "bg-slate-900/40 border border-slate-800"}`} data-testid="booking-driver-compensation-preview">
                    <div className="flex flex-col">
                      <span className={`text-[10px] uppercase tracking-widest font-mono-num font-bold ${hasDriver ? "text-emerald-300" : "text-slate-500"}`}>
                        Compenso autista {hasDriver ? `(${driverPct}%)` : "(nessun driver)"}
                      </span>
                      <span className="text-[10px] text-slate-500 mt-0.5">
                        {hasDriver ? <>Calcolato su {fmtEUR(effectiveDriverBase)}</> : "Assegna un autista per attivare il compenso"}
                      </span>
                    </div>
                    <span className={`font-mono-num text-lg font-bold ${hasDriver ? "text-emerald-400" : "text-slate-500"}`} data-testid="booking-driver-cost-auto">{fmtEUR(computedDriverCost)}</span>
                  </div>
                  </>}

                  <MoneyRow label="Commissione" v={f.commission} onChange={(v)=>set("commission",v)}/>
                  <MoneyRow label="Altri costi" v={f.other_costs} onChange={(v)=>set("other_costs",v)}/>
                  <MoneyRow label="Già incassato dal driver (cash)" v={f.driver_collected} onChange={(v)=>set("driver_collected",v)} testid="booking-driver-collected"/>
                  <MoneyRow label="Compenso già anticipato" v={f.driver_advanced} onChange={(v)=>set("driver_advanced",v)} testid="booking-driver-advanced"/>
                </div>

                <div className="mt-4 pt-4 border-t border-slate-800/80 flex items-baseline justify-between">
                  <span className="text-sm uppercase tracking-widest text-slate-400 font-mono-num font-semibold">Margine</span>
                  <div className="text-right">
                    <div className={`font-mono-num text-[26px] font-extrabold leading-none ${margin >= 0 ? "text-emerald-400" : "text-rose-400"}`} data-testid="booking-margin">{fmtEUR(margin)}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5 font-mono-num">{marginPct.toFixed(1)}%</div>
                  </div>
                </div>
              </Card>
            )}

            {isDriver && (
              <Card icon={Euro} title="Il Tuo Compenso">
                <div className="font-mono-num text-3xl font-bold text-emerald-400" data-testid="driver-compensation">{fmtEUR(f.driver_cost || 0)}</div>
                <div className="mt-2 text-xs text-slate-400">Base: {fmtEUR(f.driver_price || 0)} · Quota 50%</div>
              </Card>
            )}

            {!isDriver && (
              <Card icon={CreditCard} title="Pagamento">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Metodo">
                    <Select value={f.payment_method || "none"} onValueChange={(v)=>set("payment_method", v==="none"?"":v)}>
                      <SelectTrigger className={fieldCls}><SelectValue placeholder="Seleziona"/></SelectTrigger>
                      <SelectContent className="bg-slate-950 border-slate-800">
                        <SelectItem value="none">--</SelectItem>
                        <SelectItem value="contanti">Contanti</SelectItem>
                        <SelectItem value="pos">POS</SelectItem>
                        <SelectItem value="bonifico">Bonifico</SelectItem>
                        <SelectItem value="carta">Carta</SelectItem>
                        <SelectItem value="altro">Altro</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Stato">
                    <Select value={f.payment_status} onValueChange={(v)=>set("payment_status", v)}>
                      <SelectTrigger className={fieldCls}><SelectValue/></SelectTrigger>
                      <SelectContent className="bg-slate-950 border-slate-800">
                        <SelectItem value="Da incassare">Da incassare</SelectItem>
                        <SelectItem value="Parzialmente incassato">Parzialmente incassato</SelectItem>
                        <SelectItem value="Incassato">Incassato</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              </Card>
            )}

            {isEdit && !isDriver && TOURVOYAGERS_URL && booking.status !== "Cancellata" && !booking._received_via_exchange && (
              <a href={tourvoyagersLink(booking)} target="_blank" rel="noopener noreferrer" data-testid="booking-tourvoyagers-btn"
                onClick={(e) => openTourvoyagers(e, booking)}
                className="flex items-center justify-center gap-1.5 w-full py-2 rounded-md border border-amber-500/30 text-amber-300/90 text-xs hover:bg-amber-500/10 transition-colors">
                <Globe2 size={14}/> Crea pacchetto su Tourvoyagers
              </a>
            )}
            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1 bg-transparent border-slate-800 text-slate-300 hover:bg-slate-900 hover:text-white" onClick={() => onClose(false)}>
                {isDriver ? "Chiudi" : "Annulla"}
              </Button>
              <Button data-testid="booking-save-btn" onClick={save} disabled={saving} className="flex-1 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold gap-1.5">
                <Lock size={14} strokeWidth={3}/>
                {saving ? "Salvataggio..." : (isDriver ? "Aggiorna stato" : (isEdit ? "Aggiorna" : "Salva prenotazione"))}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ===================== Sub-componenti UI ===================== */

const fieldCls = "bg-[#0C121E] border-slate-800/80 text-slate-200 h-10";

function Card({ icon: Icon, title, children, highlighted = false, headerRight = null, sticky = false, testid }) {
  return (
    <div
      data-testid={testid}
      className={`relative rounded-2xl p-5 transition-all ${
        highlighted
          ? "bg-gradient-to-br from-[#0E1322] via-[#0B0F1A] to-[#0B0F1A] border border-amber-500/50 ring-2 ring-amber-500/25 shadow-[0_0_28px_-8px_rgba(217,169,76,0.35)]"
          : "bg-[#0B0F1A] border border-slate-800/80"
      } ${sticky ? "lg:sticky lg:top-4" : ""}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${
            highlighted ? "bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/40" : "bg-amber-500/10 text-amber-400"
          }`}>
            <Icon size={16} strokeWidth={2.2}/>
          </span>
          <span className={`text-[13px] uppercase tracking-[0.22em] font-mono-num font-bold ${highlighted ? "text-amber-300" : "text-amber-400/90"}`}>
            {title}
          </span>
        </div>
        {headerRight}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-[11px] text-slate-500 mb-1 block font-medium">{label}</Label>
      {children}
    </div>
  );
}

function MoneyRow({ label, v, onChange, testid, disabled = false, disabledHint = "" }) {
  return (
    <div className={`flex items-center justify-between gap-3 ${disabled ? "opacity-50" : ""}`}>
      <span className="text-[13px] text-slate-400 flex-1" title={disabled ? disabledHint : ""}>{label}</span>
      <div className="relative">
        <Input
          type="number" step="0.01" value={v ?? ""}
          data-testid={testid}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="w-28 pr-7 text-right bg-[#0C121E] border-slate-800/80 font-mono-num disabled:cursor-not-allowed"
          placeholder="0,00"
        />
        <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-500 font-mono-num">€</span>
      </div>
    </div>
  );
}

function ShiftWarning({ testid, tone, title, body }) {
  const palette = {
    rose: "border-rose-500/40 bg-rose-500/10 text-rose-200 strong:text-rose-300",
    indigo: "border-indigo-500/50 bg-indigo-500/10 text-indigo-200",
    purple: "border-purple-500/50 bg-purple-500/10 text-purple-200",
  }[tone] || "border-slate-700 bg-slate-800/40 text-slate-200";
  const iconColor = { rose: "text-rose-400", indigo: "text-indigo-400", purple: "text-purple-400" }[tone] || "text-slate-400";
  return (
    <div data-testid={testid} className={`mt-2 flex items-start gap-2 rounded-lg border p-2.5 text-xs ${palette}`}>
      <AlertTriangle size={16} className={`${iconColor} flex-shrink-0 mt-0.5`}/>
      <div>
        <b>{title}</b>{body}
      </div>
    </div>
  );
}

const EXPENSE_CATEGORIES = [
  { value: "carburante", label: "Carburante" },
  { value: "pedaggi", label: "Pedaggi" },
  { value: "multe", label: "Multe" },
  { value: "parcheggi", label: "Parcheggi" },
  { value: "altre", label: "Altre" },
];
const CHARGE_TO_OPTIONS = [
  { value: "azienda", label: "Azienda (rimborso)", tone: "text-emerald-400" },
  { value: "autista", label: "Autista (a suo carico)", tone: "text-slate-400" },
  { value: "da_recuperare", label: "Da recuperare (DARE)", tone: "text-rose-400" },
];

export function ExpenseItems({ items, onChange }) {
  const add = () => onChange([...(items || []), { category: "carburante", amount: 0, charge_to: "autista", note: "" }]);
  const upd = (i, k, v) => { const copy = [...items]; copy[i] = { ...copy[i], [k]: v }; onChange(copy); };
  const rm = (i) => { const copy = items.filter((_, idx) => idx !== i); onChange(copy); };
  const total = (items || []).reduce((s, x) => s + (Number(x.amount) || 0), 0);
  return (
    <div className="rounded-lg border border-slate-800 bg-slate-950/50 p-3" data-testid="expense-items">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-widest text-slate-500 font-mono-num">Spese Autista Dettagliate</span>
        <button type="button" onClick={add} data-testid="add-expense-btn" className="text-xs text-amber-400 hover:text-amber-300 inline-flex items-center gap-1 font-bold"><Plus size={12}/> Aggiungi</button>
      </div>
      {(items || []).length === 0 ? (
        <div className="text-xs text-slate-500 italic py-2">Nessuna spesa registrata</div>
      ) : (
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`expense-row-${i}`}>
              <Select value={it.category || "altre"} onValueChange={(v)=>upd(i, "category", v)}>
                <SelectTrigger className="col-span-4 bg-slate-900 border-slate-800 h-8 text-xs"><SelectValue/></SelectTrigger>
                <SelectContent className="bg-slate-950 border-slate-800">
                  {EXPENSE_CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="number" step="0.01" value={it.amount || 0} onChange={(e)=>upd(i, "amount", e.target.value)}
                className="col-span-2 bg-slate-900 border-slate-800 h-8 font-mono-num text-right text-xs" placeholder="€"/>
              <Select value={it.charge_to || "autista"} onValueChange={(v)=>upd(i, "charge_to", v)}>
                <SelectTrigger className="col-span-4 bg-slate-900 border-slate-800 h-8 text-xs"><SelectValue/></SelectTrigger>
                <SelectContent className="bg-slate-950 border-slate-800">
                  {CHARGE_TO_OPTIONS.map(c => <SelectItem key={c.value} value={c.value}><span className={c.tone}>{c.label}</span></SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="text" value={it.note || ""} onChange={(e)=>upd(i, "note", e.target.value)}
                className="col-span-1 bg-slate-900 border-slate-800 h-8 text-xs" placeholder="Nota"/>
              <button type="button" onClick={()=>rm(i)} className="col-span-1 text-rose-400 hover:text-rose-300 flex justify-center" data-testid={`rm-expense-${i}`}><Trash2 size={13}/></button>
            </div>
          ))}
          <div className="flex items-center justify-between pt-2 border-t border-slate-800 text-xs">
            <span className="text-slate-500 uppercase tracking-widest">Totale spese</span>
            <span className="text-amber-400 font-mono-num font-bold">{total.toFixed(2)} €</span>
          </div>
        </div>
      )}
    </div>
  );
}
